/**
 * Data hooks for the Leads module.
 *
 * Screens call these rather than the API layer directly, so cache keys,
 * invalidation and the debounce on search live in one place. A component that
 * called `listLeads` itself would have to remember to invalidate the list after
 * a convert, and eventually one of them would not.
 */
import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as leadsApi from '../../api/leads.api.js';
import * as masterDataApi from '../../api/masterData.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast, openQualifyGate } from '../ui/uiSlice.js';

/**
 * Keep the previous page's rows only while we are looking at the same list.
 *
 * `placeholderData: (prev) => prev` is right for paging — Next must not
 * collapse the table and throw away the reader's place — and wrong for a filter
 * change, because `data` stays defined, `isLoading` stays false, and the screen
 * shows the *old* list under the new heading with nothing saying so. Switching
 * to "My Leads" as an administrator went on reading "49 items" from All Open
 * Leads for the whole round trip, and 49 is exactly the number that view should
 * never show: the administrator owns none of them.
 *
 * So the placeholder survives a cursor change and nothing else. The cursor is
 * dropped from both sides of the comparison and the rest of the parameters are
 * compared by value, which is safe because they are all scalars — status codes,
 * a search string, a sort key.
 */
function sameList(prevQuery, params) {
  const before = prevQuery?.queryKey?.[2];
  if (!before) return false;
  const strip = ({ cursor, ...rest }) => JSON.stringify(Object.entries(rest).sort());
  return strip(before) === strip(params);
}

/**
 * A page of leads.
 *
 * @param {object} params query parameters, already debounced by the caller
 */
export function useLeads(params) {
  return useQuery({
    queryKey: queryKeys.leads.list(params),
    queryFn: () => leadsApi.listLeads(params),
    placeholderData: (prev, prevQuery) => (sameList(prevQuery, params) ? prev : undefined),
  });
}

/**
 * Every lead in the current filter, for the kanban.
 *
 * The board has to be complete or its column counts are lies — "Qualified 9"
 * with only the first page loaded says 4. There is no non-paginated form of the
 * endpoint, so this asks for the API's maximum page (200) and the screen says
 * plainly when the answer was truncated.
 */
export function useAllLeads(params, enabled) {
  const boardParams = { ...params, limit: 200, __board: true };
  return useQuery({
    queryKey: queryKeys.leads.list(boardParams),
    queryFn: () => leadsApi.listLeads({ ...params, limit: 200 }),
    enabled,
    placeholderData: (prev, prevQuery) => (sameList(prevQuery, boardParams) ? prev : undefined),
  });
}

/** One lead, for the record page. @param {string} id */
export function useLead(id) {
  return useQuery({
    queryKey: queryKeys.leads.detail(id),
    queryFn: () => leadsApi.getLead(id),
    enabled: Boolean(id),
  });
}

/**
 * Convert a lead into an account, a contact and optionally an opportunity.
 *
 * Invalidates leads *and* accounts, contacts and opportunities, because one call
 * has created records in all four — leaving the account list stale would show a
 * user the account they just created is missing.
 */
export function useConvertLead() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, options }) => leadsApi.convertLead(id, options),
    onSuccess: (result) => {
      for (const key of [
        queryKeys.leads.all, queryKeys.accounts.all,
        queryKeys.contacts.all, queryKeys.opportunities.all, queryKeys.activities.all,
      ]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      const made = [
        result.account?.created && `account ${result.account.account_no}`,
        result.contact?.created && `contact ${result.contact.contact_no}`,
        result.opportunity?.created && `opportunity ${result.opportunity.opportunity_no}`,
      ].filter(Boolean);
      dispatch(pushToast({
        tone: 'success',
        message: 'Lead converted',
        detail: made.length ? `Created ${made.join(', ')}.` : 'Linked to existing records.',
      }));
    },
    onError: (error) => {
      /* The dialog handles a name clash itself (offers the existing account). */
      if (error?.code === 'account_name_conflict') return;
      dispatch(pushToast({
        tone: 'danger',
        message: 'Could not convert this lead',
        detail: error.message,
      }));
    },
  });
}

/**
 * Patch one lead under optimistic locking.
 *
 * A `409 stale_write` is reported as its own message rather than as a generic
 * failure, because the two need different actions from the user: a validation
 * error means fix the value, a stale write means somebody else changed the row
 * and the value you are looking at is out of date.
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => leadsApi.updateLead(id, patch, rowVersion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.leads.all }),
    onError: (error, variables) => {
      /* The API refuses a move to Qualified until the discovery checklist is
         answered. Every screen that changes a status goes through this hook, so
         opening the checklist here covers the detail page, the list's inline
         edit, the board and the edit form without each knowing about it. */
      if (error?.code === 'qualification_incomplete' && variables?.patch?.status_id) {
        dispatch(openQualifyGate({ leadId: variables.id, statusId: variables.patch.status_id }));
        return;
      }
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this lead' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

/** Create a lead. Carries an Idempotency-Key, so a double-submit makes one row. */
export function useCreateLead() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => leadsApi.createLead(payload),
    onSuccess: (lead) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
      dispatch(pushToast({
        tone: 'success',
        message: `Created ${lead.full_name ?? 'the lead'}`,
        detail: lead.lead_no ? `Lead ${lead.lead_no}, scored ${lead.score ?? '—'}.` : undefined,
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not create the lead', detail: error.message }));
    },
  });
}

/** Soft-delete one lead. `reason` is mandatory at the database level. */
export function useDeleteLead() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => leadsApi.deleteLead(id, { reason, rowVersion }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
      dispatch(pushToast({
        tone: 'success',
        message: `Deleted ${variables.label ?? 'the lead'}`,
        detail: 'Soft delete — POST /leads/{id}/restore brings it back.',
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not delete that lead', detail: error.message }));
    },
  });
}

/**
 * Apply one patch, or a delete, across a selection.
 *
 * Reports partial success, because that is the honest outcome of N separate
 * writes: "34 of 40 updated" plus the reason the other six refused tells the
 * user what to do next, where a single "Bulk update failed" does not. §1
 * specifies exactly this shape for the bulk endpoints; those are 501, so the
 * loop is client-side and the contract is kept by hand.
 */
export function useBulkLeads() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ rows, patch, reason }) =>
      (reason !== undefined
        ? leadsApi.deleteLeadsBulk(rows, reason)
        : leadsApi.patchLeadsBulk(rows, patch)),
    onSuccess: ({ ok, failed }, { verb = 'updated' }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
      if (!failed.length) {
        dispatch(pushToast({
          tone: 'success',
          message: `${ok.length} lead${ok.length === 1 ? '' : 's'} ${verb}`,
        }));
        return;
      }
      dispatch(pushToast({
        tone: ok.length ? 'warning' : 'danger',
        message: `${ok.length} of ${ok.length + failed.length} leads ${verb}`,
        /* The first refusal, named. All of them would be a wall of identical
           text when the cause is the same for every row, which it usually is. */
        detail: `${failed.length} refused — ${failed[0].message}`,
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Bulk action failed', detail: error.message }));
    },
  });
}

/**
 * Change ownership on one or several leads in one call — real now:
 * `POST /leads/bulk-assign`, gated `leads:assign` (admin/manager in the seeded
 * roles). Same partial-success reporting as `useBulkLeads`, and used for a
 * single lead too rather than branching on count — one code path, consistent
 * with how Change Status and Delete already treat "one selected" as a
 * one-element bulk action rather than a special case.
 *
 * `ownerId: null` is "Transfer to queue" — the toast says so rather than
 * "reassigned to nobody", and a refusal here is most often the server's
 * New-only rule for that path, not a generic failure.
 */
export function useAssignLeads() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ ids, ownerId, reason }) => leadsApi.bulkAssignLeads(ids, { ownerId, reason }),
    onSuccess: ({ ok, failed }, { ownerId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
      const verb = ownerId === null ? 'moved to the unassigned queue' : 'reassigned';
      if (!failed.length) {
        dispatch(pushToast({
          tone: 'success',
          message: `${ok.length} lead${ok.length === 1 ? '' : 's'} ${verb}`,
        }));
        return;
      }
      dispatch(pushToast({
        tone: ok.length ? 'warning' : 'danger',
        message: `${ok.length} of ${ok.length + failed.length} leads ${verb}`,
        detail: `${failed.length} refused — ${failed[0].message}`,
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Reassignment failed', detail: error.message }));
    },
  });
}

/**
 * The picker options both the New Lead form and the record page's inline
 * edits need — statuses, sources, industries, countries, owners.
 *
 * One hook, not four separate `useQuery` calls at each call site, so the
 * cache key and the `{value, label}` shape are defined once.
 */
export function useLeadOptions() {
  const ENTITIES = ['lead_statuses', 'lead_sources', 'industries', 'countries', 'products'];

  const md = useQueries({
    queries: ENTITIES.map((entity) => ({
      queryKey: queryKeys.masterData.options(entity),
      queryFn: () => masterDataApi.listRecords(entity, { limit: 200, is_active: 'true', with_usage: 'false' }),
      staleTime: 300_000,
    })),
  });

  const users = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    staleTime: 300_000,
  });

  /* Pull the four payloads out before memoising. `useQueries` returns a new
     array on every render, so a dependency on `md` itself would recompute every
     time and the memo would be decoration. */
  const [statusRows, sourceRows, industryRows, countryRows, productRows] =
    md.map((q) => q.data?.data ?? []);
  const loading = md.some((q) => q.isLoading) || users.isLoading;
  const userRows = users.data?.data ?? [];

  return useMemo(() => ({
    statuses: statusRows.map((r) => ({ value: r.id, label: r.name })),
    /* The status the API itself would pick for a new lead, so the form opens on
       the same value `POST /leads` would have chosen if the field were omitted. */
    defaultStatusId: statusRows.find((r) => r.is_default)?.id ?? statusRows[0]?.id,
    sources: sourceRows.map((r) => ({ value: r.id, label: r.name })),
    industries: industryRows.map((r) => ({ value: r.id, label: r.name })),
    countries: countryRows.map((r) => ({ value: r.id, label: r.name })),
    products: productRows.map((r) => ({ value: r.id, label: r.name })),
    owners: userRows.map((u) => ({ value: u.id, label: u.full_name ?? u.email })),
    loading,
  }), [statusRows, sourceRows, industryRows, countryRows, productRows, userRows, loading]);
}
